import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLiveClasses, type LiveClass } from "@/hooks/useComms";
import { useAuth } from "@/hooks/useAuth";
import { resolveStudentIdentity } from "@/lib/profile";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import StudentLiveClassRoom from "../components/LiveClassRoom";

const apiBase = ((import.meta.env.VITE_API_URL as string | undefined) || (import.meta.env.PROD ? "/api" : "http://localhost:8080/api")).replace(/\/$/, "");

const statusLabels: Record<string, string> = {
  planned: "Yaklaşıyor",
  live: "Canlı",
  ended: "Tamamlandı",
  cancelled: "İptal",
};

const statusColors: Record<string, string> = {
  planned: "bg-secondary-100 text-secondary-800",
  live: "bg-red-50 text-red-700",
  ended: "bg-background-100 text-foreground-600",
  cancelled: "bg-background-100 text-foreground-400 line-through",
};

export default function CanliDersler() {
  const { data, loading, error, reload } = useLiveClasses();
  const { user } = useAuth();
  const identity = resolveStudentIdentity(user);
  const [activeClass, setActiveClass] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setInterval(reload, 10000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const classes = data ?? [];
  const liveClass = classes.find((lc) => lc.id === activeClass);

  const joinClass = useCallback(async (lc: LiveClass) => {
    if (lc.status !== "live") return;
    setJoinError(null);
    try {
      const response = await fetch(`${apiBase}/meeting/bridge-ticket`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ class_id: lc.id }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ticket) throw new Error(payload.error || "Canlı ders bağlantısı hazırlanamadı.");
      window.location.assign(`dersdinle-toplanti://join?ticket=${encodeURIComponent(payload.ticket)}`);
    } catch (error) {
      setJoinError(error instanceof Error ? error.message : 'Derse bağlantı kurulamadı. Yeniden deneyin.');
    }
  }, []);

  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  if (liveClass) {
    return (
      <StudentLiveClassRoom
        title={liveClass.title}
        roomCode={liveClass.roomCode}
        studentName={identity.name}
        hostIdentity={liveClass.teacherId}
        onExit={() => setActiveClass(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {joinError && <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-700">{joinError}</div>}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Canlı Dersler</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Yaklaşan canlı derslerin ve kayıtlar burada.
          </p>
        </div>
        {classes.some((lc) => lc.status === "live") && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-sm font-medium whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
            Şu an canlı ders var
          </span>
        )}
      </div>

      {/* Ders listesi */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {classes.map((lc) => (
          <div
            key={lc.id}
            className="bg-background-50 rounded-xl border border-background-200 p-5 hover:border-primary-300 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${statusColors[lc.status]}`}>
                {statusLabels[lc.status]}
                {lc.status === "live" && (
                  <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
                )}
              </span>
              <span className="text-xs text-foreground-400">{lc.duration} dk</span>
            </div>

            <h3 className="text-base font-semibold text-foreground-950 mb-1">{lc.title}</h3>
            <p className="text-sm text-foreground-500 mb-3">{lc.subject} · {lc.grade}</p>

            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden">
                <img src={lc.teacherAvatar} alt="" className="w-full h-full object-cover" />
              </div>
              <span className="text-sm text-foreground-700">{lc.teacher}</span>
            </div>

            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {lc.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true"></i>
                {lc.time}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" aria-hidden="true"></i>
                {lc.participants}/{lc.maxParticipants}
              </span>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">{lc.description}</p>

            <div className="flex items-center gap-2">
              {lc.status === "live" && (
                <button
                  type="button"
                  onClick={() => joinClass(lc)}
                  className="flex-1 h-10 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <i className="ri-live-line" aria-hidden="true"></i>
                  Derse Katıl
                </button>
              )}
              {lc.status === "planned" && (
                <span className="flex-1 h-10 rounded-md bg-secondary-100 text-secondary-800 text-sm font-medium inline-flex items-center justify-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {lc.time} Başlayacak
                </span>
              )}
              {lc.status === "ended" && lc.hasRecording && (
                <Link
                  to={`/ogrenci/canli-ders/${lc.id}/kayit`}
                  className="flex-1 h-10 rounded-md bg-accent-100 text-accent-800 text-sm font-semibold hover:bg-accent-200 transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <i className="ri-record-circle-line" aria-hidden="true"></i>
                  Kaydı İzle
                </Link>
              )}
              {lc.status === "ended" && !lc.hasRecording && (
                <span className="flex-1 h-10 rounded-md bg-background-100 text-foreground-400 text-sm font-medium inline-flex items-center justify-center gap-1.5">
                  Kayıt Yok
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
